import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Monitor, Smartphone, Download, ShieldCheck, Loader2, AlertTriangle, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  buildSetupScript, buildBat, getPlatform, buildServerConfigString,
  APP_STORE_URL, PLAY_STORE_URL, RD_DL, REMOTE_SETUP_VERSION, triggerFileDownload,
} from "@/pages/admin/desktop/RemoteDesktopApp";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RdSetup() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [copied, setCopied] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const platform = getPlatform();
  const isPhone = platform === "android" || platform === "ios";
  const autoRan = useRef(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/desktop/remote-setup-info/${token}`)
      .then((r) => setState({ loading: false, ...r.data }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || "This setup link is invalid." }));
  }, [token]);

  const copy = (text, tag) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(tag);
      setTimeout(() => setCopied(""), 2500);
    });
  };

  const download = useCallback(() => {
    const name = (state.name || "RustDesk").replace(/[^a-z0-9 _-]/gi, "") || "RustDesk";
    const bat = buildBat(buildSetupScript(state.password, token, state.server || null));
    const blob = new Blob([bat], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}-Setup-${REMOTE_SETUP_VERSION}.bat`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [state.name, state.password, state.server, token]);

  // Auto-start the right thing once the link info loads:
  // computer → the .bat downloads itself; Android → the APK downloads itself.
  useEffect(() => {
    if (state.loading || state.error || state.expired || state.used || autoRan.current) return;
    autoRan.current = true;
    setTimeout(() => {
      if (platform === "windows") download();
      else if (platform === "android") triggerFileDownload(RD_DL("android"));
    }, 900);
  }, [download, platform, state.error, state.expired, state.loading, state.used]);

  const enrollPhone = async () => {
    const rid = (phoneId || "").replace(/\D/g, "");
    if (!rid) return;
    setEnrolling(true);
    try {
      await axios.post(`${BACKEND}/api/desktop/remote-enroll`, { token, rustdesk_id: rid, kind: "phone" });
      setEnrolled(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not add the phone — check the ID and try again.");
    } finally {
      setEnrolling(false);
    }
  };

  const cfg = state.server ? buildServerConfigString(state.server) : "";

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 grid place-items-center p-4" data-testid="rd-setup-page">
      <div className="w-full max-w-md rounded-2xl bg-slate-800 border border-slate-700 shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/20 grid place-items-center">
            {isPhone ? <Smartphone className="w-6 h-6 text-indigo-300" /> : <Monitor className="w-6 h-6 text-indigo-300" />}
          </div>
          <div>
            <h1 className="text-lg font-semibold">Remote Access Setup</h1>
            <p className="text-xs text-slate-400">Catskills Pool Supplies</p>
          </div>
        </div>

        {state.loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : state.error || state.expired ? (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-200 flex gap-2" data-testid="rd-setup-error">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>{state.error || "This setup link has expired. Ask for a new link."}</span>
          </div>
        ) : enrolled ? (
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm text-emerald-200 flex gap-2" data-testid="rd-setup-phone-done">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            <span>All set! <b>{state.name || "This phone"}</b> is now in the Remote Computers list and can be controlled from the office. You can close this page.</span>
          </div>
        ) : state.used ? (
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm text-emerald-200 flex gap-2" data-testid="rd-setup-used">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            <span>This device is already set up and connected. You can close this page.</span>
          </div>
        ) : isPhone ? (
          <>
            <p className="text-sm text-slate-300 mb-3" data-testid="rd-setup-phone-view">
              You opened this on <b>{platform === "ios" ? "an iPhone / iPad" : "an Android device"}</b> — here&apos;s the phone setup for <b>{state.name || "this device"}</b>:
            </p>
            {platform === "ios" && (
              <p className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5 mb-3">
                Heads up: iPhones can&apos;t be <b>controlled</b> remotely (Apple blocks it) — but this phone can still be used to <b>control the computers</b>.
              </p>
            )}
            <ol className="text-sm text-slate-300 list-decimal list-inside space-y-3 my-4">
              <li>
                Install the RustDesk app{platform === "android" ? " — the APK is downloading now" : ""}:
                {platform === "android" ? (
                  <div className="flex gap-2 mt-1.5">
                    <Button size="sm" className="flex-1" onClick={() => triggerFileDownload(RD_DL("android"))} data-testid="rd-setup-apk">
                      <Download className="w-4 h-4 mr-1" /> Download APK again
                    </Button>
                    <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="flex-1">
                      <Button size="sm" variant="outline" className="w-full text-slate-900" data-testid="rd-setup-play">Google Play</Button>
                    </a>
                  </div>
                ) : (
                  <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
                    <Button size="sm" className="w-full mt-1.5" data-testid="rd-setup-appstore">
                      <Download className="w-4 h-4 mr-1" /> Get RustDesk on the App Store
                    </Button>
                  </a>
                )}
              </li>
              {cfg && (
                <li>
                  Connect the app to the private server —
                  <a href={`rustdesk://config/${cfg}`} className="block mt-1.5">
                    <Button size="sm" className="w-full" data-testid="rd-setup-autoconfig">Tap to set the server in RustDesk automatically</Button>
                  </a>
                  <p className="mt-1.5 text-[12px] text-slate-400">
                    If nothing happens, enter it by hand in the app: <b>Settings → ID/Relay Server</b>:
                  </p>
                  <div className="mt-1 rounded bg-slate-900/60 border border-slate-700 px-2.5 py-1.5 text-[12px] space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 w-16 shrink-0">ID server</span>
                      <span className="font-mono font-semibold flex-1 break-all">{state.server.ip}</span>
                      <button type="button" className="text-slate-400 hover:text-white" onClick={() => copy(state.server.ip, "sip")} data-testid="rd-setup-copy-ip">
                        {copied === "sip" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 w-16 shrink-0">Key</span>
                      <span className="font-mono font-semibold flex-1 break-all">{state.server.key}</span>
                      <button type="button" className="text-slate-400 hover:text-white" onClick={() => copy(state.server.key, "skey")} data-testid="rd-setup-copy-key">
                        {copied === "skey" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">(Leave Relay/API empty — press OK.)</p>
                </li>
              )}
              {platform === "android" && (
                <>
                  <li>In the app open the <b>Share screen</b> tab (bottom bar), tap <b>Start service</b> and allow <b>screen capture</b> + <b>input control (Accessibility)</b> when asked.</li>
                  {state.password && (
                    <li>
                      In the app go to the <b>Share screen</b> tab (bottom bar), tap the <b>pencil icon next to &quot;Password&quot;</b>, choose <b>Use permanent password</b>, and enter this password:
                      <div className="flex items-center gap-2 mt-1.5 rounded bg-slate-900/60 border border-slate-700 px-2.5 py-1.5">
                        <span className="font-mono font-semibold flex-1 break-all">{state.password}</span>
                        <button type="button" className="text-slate-400 hover:text-white" onClick={() => copy(state.password, "pw")} data-testid="rd-setup-copy-pw">
                          {copied === "pw" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </li>
                  )}
                  <li>
                    Type the <b>ID</b> from the app&apos;s home screen here, and you&apos;re done:
                    <div className="flex gap-2 mt-1.5">
                      <Input value={phoneId} onChange={(e) => setPhoneId(e.target.value)} placeholder="e.g. 123 456 789" inputMode="numeric" className="bg-slate-900/60 border-slate-700 text-slate-100" data-testid="rd-setup-phone-id" />
                      <Button size="sm" className="shrink-0 h-10" disabled={enrolling || !phoneId.replace(/\D/g, "")} onClick={enrollPhone} data-testid="rd-setup-phone-add">
                        {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add this phone"}
                      </Button>
                    </div>
                  </li>
                </>
              )}
            </ol>
            {platform === "ios" && (
              <p className="text-[11px] text-slate-500 text-center">After installing and pasting the code, you can connect to any computer using its ID and password.</p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-slate-300 mb-1" data-testid="rd-setup-pc-view">
              This will set up <b>{state.name || "this computer"}</b> for remote access.
              {platform === "windows" && <> The setup file is <b>downloading automatically</b>.</>}
            </p>
            <ol className="text-sm text-slate-400 list-decimal list-inside space-y-1.5 my-4">
              <li>{platform === "windows" ? "Open the downloaded setup file (or download it again below)." : "Click the button below to download the setup file."}</li>
              <li><b>Double-click</b> the downloaded file.</li>
              <li>Click <b>Yes</b> when Windows asks for administrator permission.</li>
              <li>Wait — it finishes and closes by itself. That&apos;s it.</li>
            </ol>
            <Button className="w-full h-11 text-base" onClick={download} data-testid="rd-setup-download">
              <Download className="w-5 h-5 mr-2" /> Download setup file (.bat)
            </Button>
            <p className="text-[11px] text-slate-500 mt-3 text-center">
              Windows only. If a security prompt appears, choose &quot;More info → Run anyway&quot; — this is your own setup file.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
